import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaImage, FaInfoCircle, FaCode, FaCheckCircle, FaHeart, FaStar } from 'react-icons/fa';
import { businessNetworkApi } from '../api/businessNetworkApi';
import { userApi } from '../api/userApi';
import appConfig from '../utils/appConfig';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export const ProductDetailPage = () => {
  const { networkId, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [network, setNetwork] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await businessNetworkApi.fetchNetwork(networkId);

        if (response?.result && response.result.length > 0) {
          const networkData = response.result[0];
          setNetwork(networkData);

          const foundProduct = networkData.listings?.find(
            item => item.productListItemID === parseInt(productId)
          );

          if (foundProduct) {
            setProduct(foundProduct);

            // Fetch owner information
            if (foundProduct.walletUserRegistrationID) {
              try {
                const ownerResult = await userApi.findUserByIdFull(foundProduct.walletUserRegistrationID, networkId);
                if (ownerResult.user) {
                  setOwner(ownerResult.user);
                }
              } catch (err) {
                console.warn('Failed to fetch owner:', err);
              }
            }
          } else {
            setError('Product not found');
          }
        } else {
          setError('Network data not found');
        }
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError(err.message || 'Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (networkId && productId) {
      fetchProductDetails();
    }
  }, [networkId, productId]);

  if (loading) return <LoadingSpinner />;

  const isSingleNetworkMode = appConfig.isSingleNetworkMode();

  if (error) {
    return (
      <div className="fade-in">
        {!isSingleNetworkMode && (
          <button
            className="btn mb-4"
            onClick={() => navigate(-1)}
          style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '0.9rem',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
            <FaArrowLeft className="me-2" />
            Back to Listings
          </button>
        )}
        <div className="alert alert-danger">Error: {error}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="fade-in">
        {!isSingleNetworkMode && (
          <button
            className="btn mb-4"
            onClick={() => navigate(-1)}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            <FaArrowLeft className="me-2" />
            Back to Listings
          </button>
        )}
        <div className="alert alert-warning">Product not found</div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Enhanced Back Button */}
      {!isSingleNetworkMode && (
        <button
          className="btn mb-4"
          onClick={() => navigate(-1)}
        style={{
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          padding: '12px 24px',
          fontSize: '0.9rem',
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 8px 20px rgba(99, 102, 241, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
        }}
          >
            <FaArrowLeft className="me-2" />
            Back to Business Listings
          </button>
      )}

      {/* Product Detail Container */}
      <div className="card shadow-lg" style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(99, 102, 241, 0.1)',
        overflow: 'hidden'
      }}>

        {/* Header Section */}
        <div style={{
          position: 'relative',
          minHeight: '300px',
          background: product.productImageUrl
            ? `linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(79, 70, 229, 0.9)), url(${product.productImageUrl})`
            : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '40px',
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: '30px'
          }}>
            {/* Product Logo */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '20px',
              minWidth: '120px',
              minHeight: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(10px)'
            }}>
              {product.productLogoUrl ? (
                <img
                  src={product.productLogoUrl}
                  alt={`${product.productName} Logo`}
                  style={{
                    maxHeight: '80px',
                    maxWidth: '80px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div style={{
                display: product.productLogoUrl ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                fontSize: '2rem'
              }}>
                <FaImage />
              </div>
            </div>

            {/* Product Info */}
            <div style={{ flex: 1 }}>
              <h1 style={{
                margin: '0 0 15px 0',
                fontWeight: '700',
                fontSize: '3rem',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                lineHeight: '1.2'
              }}>
                {product.productName}
              </h1>

              <div style={{ marginBottom: '20px' }}>
                <span style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '10px 20px',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                  {product.productCategoryName || product.recordTypeName || 'Uncategorized'}
                </span>
              </div>

              {/* Status Badges */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {product.isVerified && (
                  <span style={{
                    background: 'rgba(16, 185, 129, 0.9)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(10px)',
                    fontWeight: '500'
                  }}>
                    <FaCheckCircle />
                    Verified
                  </span>
                )}
                {product.isHot && (
                  <span style={{
                    background: 'rgba(245, 158, 11, 0.9)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(10px)',
                    fontWeight: '500'
                  }}>
                    <FaStar />
                    Hot
                  </span>
                )}
                {product.isFavorite && (
                  <span style={{
                    background: 'rgba(236, 72, 153, 0.9)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backdropFilter: 'blur(10px)',
                    fontWeight: '500'
                  }}>
                    <FaHeart />
                    Favorite
                  </span>
                )}
                {product.isSponsored && (
                  <span style={{
                    background: 'rgba(139, 92, 246, 0.9)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    backdropFilter: 'blur(10px)',
                    fontWeight: '500'
                  }}>
                    Sponsored
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div style={{ padding: '40px' }}>

          {/* Product Code */}
          <div style={{ marginBottom: '30px' }}>
            <h5 style={{
              color: '#374151',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaCode style={{ color: '#6b7280' }} />
              Product Code
            </h5>
            <code style={{
              background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
              padding: '15px 20px',
              borderRadius: '12px',
              fontSize: '1rem',
              color: '#374151',
              display: 'block',
              wordBreak: 'break-all',
              border: '1px solid rgba(148, 163, 184, 0.2)'
            }}>
              {product.productCode}
            </code>
          </div>

          {/* Descriptions */}
          <div className="row g-4 mb-4">
            {product.productDescription && (
              <div className="col-12">
                <h5 style={{
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Description
                </h5>
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  color: '#374151',
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}>
                  {product.productDescription}
                </div>
              </div>
            )}

            {product.productShortDescription && (
              <div className="col-md-6">
                <h5 style={{
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Short Description
                </h5>
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  padding: '15px',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>
                  {product.productShortDescription}
                </div>
              </div>
            )}

            {product.productLongDescription && (
              <div className="col-md-6">
                <h5 style={{
                  color: '#374151',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Long Description
                </h5>
                <div style={{
                  background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                  padding: '15px',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>
                  {product.productLongDescription}
                </div>
              </div>
            )}
          </div>

          {/* Technical Details */}
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            padding: '25px',
            borderRadius: '16px',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            marginBottom: '30px'
          }}>
            <h5 style={{
              color: '#374151',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaInfoCircle style={{ color: '#6b7280' }} />
              Technical Information
            </h5>
            <div className="row g-4">
              <div className="col-md-6">
                <div style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  padding: '15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(148, 163, 184, 0.2)'
                }}>
                  <small className="text-muted d-block mb-1">Product ID</small>
                  <strong style={{ color: '#374151', fontSize: '1.1rem' }}>{product.productListItemID}</strong>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  padding: '15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(148, 163, 184, 0.2)'
                }}>
                  <small className="text-muted d-block mb-1">Record Type ID</small>
                  <strong style={{ color: '#374151', fontSize: '1.1rem' }}>{product.recordTypeID || 'N/A'}</strong>
                </div>
              </div>
              {network?.subDomain && (
                <div className="col-md-6">
                  <div style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '1px solid rgba(148, 163, 184, 0.2)'
                  }}>
                    <small className="text-muted d-block mb-1">Business Network</small>
                    <strong style={{ color: '#374151', fontSize: '1.1rem' }}>{network.subDomain}</strong>
                  </div>
                </div>
              )}
              <div className="col-md-6">
                <div style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  padding: '15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(148, 163, 184, 0.2)'
                }}>
                  <small className="text-muted d-block mb-1">Product Owner</small>
                  <strong style={{ color: '#374151', fontSize: '1.1rem', wordBreak: 'break-all' }}>
                    {owner?.rootIdentityUrl || `User ID: ${product.walletUserRegistrationID}`}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* Status Information */}
          <div style={{
            background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)',
            padding: '25px',
            borderRadius: '16px',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            marginBottom: '30px'
          }}>
            <h5 style={{
              color: '#92400e',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaCheckCircle style={{ color: '#f59e0b' }} />
              Status & Properties
            </h5>
            <div className="row g-3">
              <div className="col-md-3">
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  borderRadius: '12px',
                  background: product.isPublic ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '5px' }}>Public</div>
                  <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{product.isPublic ? 'YES' : 'NO'}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  borderRadius: '12px',
                  background: product.isVerified ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '5px' }}>Verified</div>
                  <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{product.isVerified ? 'YES' : 'NO'}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  borderRadius: '12px',
                  background: product.isHot ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '5px' }}>Hot</div>
                  <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{product.isHot ? 'YES' : 'NO'}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  borderRadius: '12px',
                  background: product.isFavorite ? 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '5px' }}>Favorite</div>
                  <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{product.isFavorite ? 'YES' : 'NO'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Image */}
          {product.productImageUrl && (
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{
                color: '#374151',
                fontWeight: '600',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FaImage style={{ color: '#6b7280' }} />
                Product Image
              </h5>
              <div style={{
                textAlign: 'center',
                padding: '30px',
                background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(148, 163, 184, 0.2)'
              }}>
                <img
                  src={product.productImageUrl}
                  alt={`${product.productName} Image`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{
                  display: 'none',
                  color: '#6b7280',
                  fontStyle: 'italic',
                  fontSize: '1.1rem'
                }}>
                  Image not available
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};